import { useCallback, useEffect, useMemo, useState } from 'react';
import { mapEntityParams, predefinedTemplates } from 'constants/entities';
import { errorMessages } from 'constants/errors';
import { MAP_TOOLS_INFO_POPUP } from 'constants/modals';
import { NOTHING_WAS_FOUND_MESSAGE } from 'constants/routes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEscapeDownControlled } from 'hooks/useEscapeDown';
import { useFavoriteObjects } from 'hooks/useFavoriteObjects';
import { useMapDraw } from 'hooks/useMapDraw';
import { useUnmount } from 'hooks/useUnmount';
import { ReactComponent as CircleIcon } from 'images/newIcons/circle.svg';
import { ISelectOption } from 'interfaces';
import { commonActions } from 'store/slices/mapV2/mapReducer/toolsReducer/commonSlice';
import { commonToolSelector } from 'store/slices/mapV2/mapReducer/toolsReducer/commonSlice/selectors';
import { CommonTool } from 'store/slices/mapV2/mapReducer/toolsReducer/commonSlice/types';
import { stateActions } from 'store/slices/mapV2/mapReducer/toolsReducer/stateSlice';
import { interactionModeSelector } from 'store/slices/mapV2/mapReducer/toolsReducer/stateSlice/selectors';
import { predefinedTemplateSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { modalsActions } from 'store/slices/service/modalsSlice';
import { MapCommonModes, MapDrawModes, PredefinedTemplate } from 'types';
import { useDebouncedCallback } from 'use-debounce';

import { Loader, TextInput } from 'components/ui';
import SvgIcon from 'components/ui/SvgIcon';
import { findOptionByValue, getMapLayerOptions, notify } from 'utils';
import {
  useDefaultProjectSettingsLocalStorage,
  useRecentProjectsLocalStorage,
} from 'utils/localStorage';

import { LineTextSelect, LineTextTreeSelect } from '../LineTextSelect';

import { SUBPROJECT_TYPES_OPTION_LIST } from './constants';
import { QuickEntitySection } from './QuickEntitySection';
import { QuickEntity, QuickObjectsById, SubParentTypes } from './types';
import { extractIcons, getSubParentName } from './utils';

export const MapQuickEntities = () => {
  const [
    defaultProjectSettings,
    setDefaultProjectId,
    setDefaultSubProjectType,
  ] = useDefaultProjectSettingsLocalStorage();
  const [recentProjects, upsertRecentProject] = useRecentProjectsLocalStorage();
  const interactionMode = useAppSelector(interactionModeSelector);
  const commonTool = useAppSelector(commonToolSelector);
  const mapLayerTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_LAYER)
  );
  const mapObjectTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_OBJECT)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [quickObjectsById, setQuickObjectsById] = useState<QuickObjectsById>(
    {}
  );
  const [searchDisplayQuery, setSearchDispayQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [parentEntityId, setParentEntityId] = useState(
    Number(defaultProjectSettings?.projectId) || null
  );
  const [subParentType, setSubParentType] = useState(
    defaultProjectSettings?.subProjectType ?? SubParentTypes.NONE
  );
  const [parentEntityOptions, setParentEntityOptions] = useState<
    ISelectOption<number>[]
  >([]);

  const dispatch = useAppDispatch();

  const { handleDrawClear, handleDrawModeChange } = useMapDraw();
  const { favorites, toggleFavorite } = useFavoriteObjects();

  const quickObjects = useMemo(
    () => Object.values(quickObjectsById),
    [quickObjectsById]
  );

  const favoriteQuickObjects = useMemo(
    () => quickObjects.filter((obj) => obj.isFavorite),
    [quickObjects]
  );

  const restQuickObjects = useMemo(
    () => quickObjects.filter((obj) => !obj.isFavorite),
    [quickObjects]
  );

  const searchedFavoriteObjects = useMemo(
    () =>
      favoriteQuickObjects.filter((obj) =>
        searchQuery
          ? obj.type.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      ),
    [favoriteQuickObjects, searchQuery]
  );

  const searchedRestObjects = useMemo(
    () =>
      restQuickObjects.filter((obj) =>
        searchQuery
          ? obj.type.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      ),
    [favoriteQuickObjects, searchQuery]
  );

  const activeEntityId =
    commonTool?.type === MapCommonModes.create_quick_entity
      ? commonTool.value.type
      : '';

  const hasFavoriteObjects = !!searchedFavoriteObjects.length;
  const hasRestObjects = !!searchedRestObjects.length;

  const fetchedIcons = useMemo(
    () =>
      mapObjectTemplate?.parameters?.find(
        ({ title }) => title === mapEntityParams.TYPE
      )?.settings?.militaryClass ?? [],
    [mapObjectTemplate]
  );

  const processedIcons = useMemo(
    () => extractIcons(fetchedIcons),
    [fetchedIcons]
  );

  const getParentEntitiesOptions = useCallback(
    async (layerEntityTemplate: PredefinedTemplate) => {
      setIsLoading(true);
      getMapLayerOptions(layerEntityTemplate.template.id, {
        recentProjects,
      })
        .then(setParentEntityOptions)
        .catch(() => notify.error(errorMessages.GET_OBJECTS_ERROR))
        .finally(() => setIsLoading(false));
    },
    []
  );

  const getQuickObjects = useCallback(
    (
      objectEntityTemplate: PredefinedTemplate,
      favorites: Record<string, boolean>
    ) => {
      const quickObjects =
        objectEntityTemplate.parameters
          .find((item) => item.title === mapEntityParams.TYPE)
          ?.settings?.allowedValues?.map((value) => {
            const iconData = processedIcons.find((icon) => icon.Name === value);

            return {
              id: value,
              type: value,
              isFavorite: favorites[value],
              icon: iconData?.Svg ? (
                <SvgIcon svgString={iconData.Svg} />
              ) : (
                <CircleIcon width={24} height={24} />
              ),
            };
          }) ?? [];

      const quickObjectsById = quickObjects.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {} as QuickObjectsById
      );

      setQuickObjectsById(quickObjectsById);
    },
    [processedIcons]
  );

  const getCleanup = useCallback(() => {
    if (
      interactionMode === MapCommonModes.create_quick_entity &&
      commonTool?.type === MapCommonModes.create_quick_entity
    ) {
      dispatch(stateActions.setInteractionMode(MapDrawModes.simple_select));
      dispatch(commonActions.setCommonTool(null));
    }
  }, [interactionMode, commonTool]);

  const handleParentEntityIdChange = useCallback(
    (id: number | null, currentTool: CommonTool | null) => {
      setParentEntityId(id);
      setDefaultProjectId(String(id));
      id && upsertRecentProject(id);
      currentTool?.type === MapCommonModes.create_quick_entity &&
        dispatch(commonActions.updateCommonTool({ primalParentId: id }));
    },
    [setDefaultProjectId, upsertRecentProject]
  );

  const handleSubParentTypeChange = (
    subparentType: string,
    currentTool: CommonTool | null
  ) => {
    setSubParentType(subparentType);
    currentTool?.type === MapCommonModes.create_quick_entity &&
      dispatch(
        commonActions.updateCommonTool({
          secondaryParentName: getSubParentName(
            subparentType,
            currentTool.value.type
          ),
        })
      );
    setDefaultSubProjectType(subparentType);
  };

  const handleSearchQueryChangeDebounced = useDebouncedCallback(
    setSearchQuery,
    500
  );

  const handleSearchDisplayQueryChange = (value: string) => {
    setSearchDispayQuery(value);
    handleSearchQueryChangeDebounced(value);
  };

  const handleEntityDeactivate = useCallback(() => {
    dispatch(stateActions.setInteractionMode(MapDrawModes.simple_select));
    dispatch(commonActions.setCommonTool(null));
  }, []);

  const handleEntityActivate = useCallback(
    (id: string) => {
      const subParentName = getSubParentName(subParentType, id);
      const isParentEntityId = findOptionByValue(
        parentEntityId,
        parentEntityOptions
      );

      handleDrawClear();
      handleDrawModeChange(MapDrawModes.simple_select);

      dispatch(
        stateActions.setInteractionMode(MapCommonModes.create_quick_entity)
      );
      dispatch(
        commonActions.setCommonTool({
          type: MapCommonModes.create_quick_entity,
          value: {
            type: id,
            primalParentId: isParentEntityId ? parentEntityId : null,
            secondaryParentName: subParentName,
            primalParentOptions: parentEntityOptions,
            entity: null,
            changePrimalParentId: handleParentEntityIdChange,
          },
        })
      );
      dispatch(modalsActions.removeModal(MAP_TOOLS_INFO_POPUP));
    },
    [
      subParentType,
      parentEntityId,
      parentEntityOptions,
      handleDrawModeChange,
      handleParentEntityIdChange,
    ]
  );

  const [subscribeToEscapeDown, unsubscribeFromEscapeDown] =
    useEscapeDownControlled(handleEntityDeactivate);

  const handleEntityClick = useCallback(
    (id: string, isActive?: boolean) => {
      if (isActive) {
        unsubscribeFromEscapeDown();
        handleEntityDeactivate();
      } else {
        subscribeToEscapeDown();
        handleEntityActivate(id);
      }
    },
    [handleEntityDeactivate, handleEntityActivate]
  );

  const getContextMenu = useCallback(
    (obj: QuickEntity) => [
      {
        title: obj.isFavorite ? 'Убрать из избранного' : 'Добавить в избранное',
        onClick: () => toggleFavorite(obj.id, !obj.isFavorite),
      },
    ],
    [toggleFavorite]
  );

  useEffect(() => {
    if (mapLayerTemplate) {
      getParentEntitiesOptions(mapLayerTemplate);
    }
  }, [mapLayerTemplate, getParentEntitiesOptions]);

  useEffect(() => {
    if (mapObjectTemplate) {
      getQuickObjects(mapObjectTemplate, favorites);
    }
  }, [mapObjectTemplate, favorites, getQuickObjects]);

  useUnmount(getCleanup);

  return (
    <div className="relative w-full h-full px-4 pb-4 overflow-auto">
      <div className="sticky top-0 py-4 bg-dark z-[1]">
        <div className="relative flex justify-between items-center gap-1 pb-4">
          <LineTextTreeSelect
            title="По умолчанию"
            value={parentEntityId}
            options={parentEntityOptions}
            withSearch
            classNames={{
              container: '!static max-w-[60%]',
            }}
            onSelect={(id) => handleParentEntityIdChange(id, commonTool)}
          />
          <LineTextSelect
            title="Подпапка"
            value={subParentType ?? SubParentTypes.NONE}
            options={SUBPROJECT_TYPES_OPTION_LIST}
            classNames={{
              container: 'max-w-[40%]',
            }}
            onSelect={(subParentType) =>
              handleSubParentTypeChange(subParentType, commonTool)
            }
          />
        </div>
        <TextInput
          value={searchDisplayQuery}
          placeholder="Начните поиск по типу сущности"
          searchIcon
          onChange={handleSearchDisplayQueryChange}
        />
      </div>
      <div className="flex flex-col gap-6">
        {hasFavoriteObjects || hasRestObjects ? (
          <>
            {hasFavoriteObjects && (
              <QuickEntitySection
                title="Избранное"
                entities={searchedFavoriteObjects}
                activeEntityId={activeEntityId}
                getContextMenu={getContextMenu}
                onEntityClick={handleEntityClick}
                onEntityFavoriteToggle={toggleFavorite}
              />
            )}
            {hasRestObjects && (
              <QuickEntitySection
                title="Все"
                entities={searchedRestObjects}
                activeEntityId={activeEntityId}
                getContextMenu={getContextMenu}
                onEntityClick={handleEntityClick}
                onEntityFavoriteToggle={toggleFavorite}
              />
            )}
          </>
        ) : (
          <span className="tpg-c1 text-tpg_base">
            {NOTHING_WAS_FOUND_MESSAGE}
          </span>
        )}
      </div>
      {isLoading && (
        <Loader className="absolute h-full left-0 top-0 z-10 bg-dark rounded-[10px]" />
      )}
    </div>
  );
};
